import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  MessageSquare, Calendar, Phone, Gift, Building2, Vault,
  CheckSquare, ChefHat, CalendarDays, ClipboardList, GraduationCap, FileText,
  Trash2, Sparkles, BarChart3, Users, TrendingUp, HelpCircle, Link2, Send,
  Brush, Wrench, ShieldCheck, ArrowLeftRight, ClipboardCheck, ListChecks, CalendarClock
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useRoleDetection } from '@/hooks/useRoleDetection';
import { userPreferencesService, QuickActionItem } from '@/lib/services/userPreferences';
import { settingsService } from '@/lib/services/settings';
import { isEquipmentPageOn } from '@/lib/equipmentPages';
import { hasKitchenAccess } from '@/utils/departmentAccess';
import { usePersonalTasksEnabled } from '@/hooks/usePersonalTasksEnabled';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useRewardsCurrency } from '@/hooks/useRewardsCurrency';
import { applyCurrencyName } from '@/lib/rewardsCurrency';

export const QUICK_ACTION_OPTIONS = [
  { id: 'teamChat', label: 'Team Chat', icon: MessageSquare, requiresFeature: null, requiresLeader: false, requiresDirector: false },
  { id: 'sendTask', label: 'Send a Task', icon: Send, requiresFeature: null, requiresLeader: false, requiresDirector: false },
  { id: 'timeOff', label: 'Schedule Request', icon: CalendarClock, requiresFeature: null, requiresLeader: false, requiresDirector: false },
  { id: 'calendar', label: 'Calendar', icon: Calendar, requiresFeature: 'leadership', requiresLeader: false, requiresDirector: false },
  { id: 'guestRecovery', label: 'Guest Recovery', icon: Phone, requiresFeature: null, requiresLeader: true, requiresDirector: false },
  { id: 'rewards', label: 'CFAdollars', icon: Gift, requiresFeature: null, requiresLeader: false, requiresDirector: false },
  { id: 'weeklyDigest', label: 'Weekly Digest', icon: Building2, requiresFeature: null, requiresLeader: false, requiresDirector: true },
  { id: 'safeCounting', label: 'Safe Counting', icon: Vault, requiresFeature: 'safeCounting', requiresLeader: true, requiresDirector: false },
  { id: 'foh', label: 'FOH', icon: CheckSquare, requiresFeature: 'fohTasks', requiresLeader: false, requiresDirector: false },
  { id: 'cleaning', label: 'Cleaning', icon: Sparkles, requiresFeature: 'fohTasks', requiresLeader: false, requiresDirector: false },
  { id: 'kitchen', label: 'Kitchen', icon: ChefHat, requiresFeature: 'kitchen', requiresLeader: false, requiresDirector: false },
  { id: 'bohCleaning', label: 'BOH Cleaning', icon: Brush, requiresFeature: 'kitchen', requiresLeader: false, requiresDirector: false },
  { id: 'kitchenChecklists', label: 'Kitchen Checklists', icon: ClipboardList, requiresFeature: 'kitchen', requiresLeader: false, requiresDirector: false },
  { id: 'kitchenEquipment', label: 'Kitchen Equipment', icon: Wrench, requiresFeature: 'kitchen', requiresLeader: false, requiresDirector: false },
  { id: 'foodSafety', label: 'Food Safety', icon: ShieldCheck, requiresFeature: 'kitchen', requiresLeader: false, requiresDirector: false },
  { id: 'borrowLog', label: 'Borrow Log', icon: ArrowLeftRight, requiresFeature: null, requiresLeader: false, requiresDirector: false },
  { id: 'selfAssessments', label: 'Self-Assessments', icon: ClipboardCheck, requiresFeature: 'kitchen', requiresLeader: false, requiresDirector: false },
  { id: 'wasteTracker', label: 'Waste Tracker', icon: Trash2, requiresFeature: 'kitchen', requiresLeader: false, requiresDirector: false },
  { id: 'setupSheet', label: 'Setup Sheets', icon: CalendarDays, requiresFeature: 'setups', requiresLeader: false, requiresDirector: false },
  { id: 'evaluations', label: 'Evaluations', icon: ClipboardList, requiresFeature: 'evaluations', requiresLeader: true, requiresDirector: false },
  { id: 'training', label: 'Training', icon: GraduationCap, requiresFeature: 'training', requiresLeader: false, requiresDirector: false },
  { id: 'assessments', label: 'Assessments', icon: ListChecks, requiresFeature: 'training', requiresLeader: false, requiresDirector: false },
  { id: 'documentation', label: 'Documentation', icon: FileText, requiresFeature: 'documentation', requiresLeader: true, requiresDirector: false },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, requiresFeature: null, requiresLeader: true, requiresDirector: false },
  { id: 'team', label: 'Team', icon: Users, requiresFeature: null, requiresLeader: true, requiresDirector: false },
  { id: 'leadership', label: 'Leadership', icon: TrendingUp, requiresFeature: 'leadership', requiresLeader: false, requiresDirector: false },
  { id: 'teamDevelopment', label: 'Development', icon: TrendingUp, requiresFeature: 'leadership', requiresLeader: false, requiresDirector: false },
  { id: 'vendors', label: 'Vendors', icon: Building2, requiresFeature: null, requiresLeader: false, requiresDirector: true },
  { id: 'quickLinks', label: 'Quick Links', icon: Link2, requiresFeature: null, requiresLeader: false, requiresDirector: false },
  { id: 'howTo', label: 'How To', icon: HelpCircle, requiresFeature: null, requiresLeader: false, requiresDirector: false },
  // Last and not in any default set: an opt-in store add-on people pick themselves.
  { id: 'servSafe', label: 'ServSafe', icon: ShieldCheck, requiresFeature: null, requiresLeader: false, requiresDirector: false },
];

interface QuickActionsEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuickActionsEditor({ open, onOpenChange }: QuickActionsEditorProps) {
  const { user } = useAuth();
  const currency = useRewardsCurrency();
  const { isFeatureEnabled } = useSubscription();
  const { isLeader, isDirector } = useRoleDetection();
  const personalTasksOn = usePersonalTasksEnabled();
  const queryClient = useQueryClient();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  
  const { data: userPrefs } = useQuery({
    queryKey: ['userPreferences'],
    queryFn: userPreferencesService.getUserPreferences
  });

  const { data: storeSettings } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsService.getSettings
  });

  const KITCHEN_GATED_IDS = ['kitchen', 'bohCleaning', 'kitchenChecklists', 'kitchenEquipment', 'foodSafety'];

  const availableOptions = useMemo(() => QUICK_ACTION_OPTIONS.filter(option => {
    if (option.requiresFeature && !isFeatureEnabled(option.requiresFeature)) return false;
    if (option.requiresLeader && !isLeader && !user?.isAdmin) return false;
    if (option.requiresDirector && !isDirector && !user?.isAdmin && user?.email !== 'jonp4208@gmail.com') return false;
    // Kitchen check
    if (KITCHEN_GATED_IDS.includes(option.id) && !hasKitchenAccess(user) && !isLeader) return false;
    // Per-store settings, not subscription features, so requiresFeature can't
    // express them. Without this the picker still offers a tile the grid
    // refuses to render.
    if (option.id === 'sendTask' && !personalTasksOn) return false;
    if (option.id === 'borrowLog' && !storeSettings?.userAccess?.borrowLog?.enabled) return false;
    if (option.id === 'timeOff' && !storeSettings?.userAccess?.lateTimeOff?.enabled) return false;
    if (option.id === 'servSafe' && !storeSettings?.userAccess?.servSafe?.enabled) return false;
    if (option.id === 'selfAssessments' && !storeSettings?.userAccess?.selfAssessments?.enabled) return false;
    if (option.id === 'kitchenEquipment' && !isEquipmentPageOn(storeSettings, 'kitchen')) return false;
    return true;
  }), [isFeatureEnabled, isLeader, isDirector, user, personalTasksOn, storeSettings]);

  useEffect(() => {
    let keysToSet: string[] = [];
    if (userPrefs?.uiPreferences?.quickActions?.items) {
      const activeKeys = userPrefs.uiPreferences.quickActions.items
        .filter((item: QuickActionItem) => item.show)
        .map((item: QuickActionItem) => item.key);
      if (activeKeys.length > 0) {
        keysToSet = activeKeys;
      }
    }
    
    if (keysToSet.length === 0) {
      // Defaults if none saved
      // Mirrors Layout's default grid; the availability filter below drops
      // sendTask/timeOff for stores that never turned them on.
      keysToSet = ['teamChat', 'sendTask', 'timeOff', 'calendar', 'guestRecovery', 'rewards', 'weeklyDigest', 'safeCounting'];
    }

    // Filter to only include options the user actually has access to
    const validKeys = keysToSet.filter(key => availableOptions.some(opt => opt.id === key));
    
    // Enforce max 4 items
    setSelectedItems(validKeys.slice(0, 4));
  }, [userPrefs, availableOptions]);

  const mutation = useMutation({
    mutationFn: (items: QuickActionItem[]) => userPreferencesService.updateQuickActions(items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userPreferences'] });
      toast.success('Quick Actions saved successfully!');
      onOpenChange(false);
    },
    onError: () => {
      toast.error('Failed to save Quick Actions.');
    }
  });

  const handleToggle = (id: string, checked: boolean) => {
    if (checked) {
      if (selectedItems.length >= 4) {
        toast.error('You can only pin up to 4 Quick Actions.');
        return;
      }
      setSelectedItems([...selectedItems, id]);
    } else {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    }
  };

  const handleSave = () => {
    const itemsToSave: QuickActionItem[] = selectedItems.map((key, index) => ({
      key,
      show: true,
      order: index
    }));
    mutation.mutate(itemsToSave);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] w-[95%] z-[10000]">
        <DialogHeader>
          <DialogTitle>Customize Quick Actions</DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-sm text-gray-500 mb-4">
            Select up to 4 tools to pin to your mobile menu dashboard. ({selectedItems.length}/4)
          </p>
          <div className="space-y-3 max-h-[50vh] overflow-y-auto thin-scrollbar pr-2">
            {availableOptions.map(option => {
              const Icon = option.icon;
              const isChecked = selectedItems.includes(option.id);
              return (
                <div 
                  key={option.id} 
                  className={`flex items-center space-x-3 p-3 rounded-lg border transition-colors ${isChecked ? 'border-red-200 bg-red-50/30' : 'border-gray-100'}`}
                >
                  <Checkbox 
                    id={`qa-${option.id}`} 
                    checked={isChecked}
                    onCheckedChange={(c) => handleToggle(option.id, c as boolean)}
                  />
                  <label 
                    htmlFor={`qa-${option.id}`} 
                    className="flex flex-1 items-center gap-3 cursor-pointer"
                  >
                    <div className={`p-2 rounded-md ${isChecked ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="font-medium text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      {applyCurrencyName(option.label, currency)}
                    </span>
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} className="bg-red-600 hover:bg-red-700 text-white" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save Pins'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
